import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserProfile } from '../firebase/services';

// Role types
export type UserRole = {
  roleId: number;
  createdAt?: any;
  updatedAt?: any;
};

// User profile type
type UserProfile = {
  id: string;
  roleId?: number;
  [key: string]: any;
};

// Role context type
interface RoleContextType {
  userRole: UserRole | null;
  loading: boolean;
  isAdmin: boolean;
  isClient: boolean;
  refreshRole: () => Promise<void>;
}

// Create context with default values
const RoleContext = createContext<RoleContextType>({
  userRole: null,
  loading: true,
  isAdmin: false,
  isClient: false,
  refreshRole: async () => {}
});

// Custom hook to use the role context
export const useRole = () => useContext(RoleContext);

// Role provider component
export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user role
  const fetchUserRole = async () => {
    if (!currentUser) {
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      // Get role from users collection
      const userData = await getUserProfile(currentUser.uid) as UserProfile;
      
      if (userData && userData.roleId) {
        setUserRole({ roleId: userData.roleId });
      } else {
        // If no role data, set default client role
        setUserRole({ roleId: 2 }); // Default to client role
        console.log("No role found, defaulting to client role");
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      // Default to client role if there's an error
      setUserRole({ roleId: 2 });
    } finally {
      setLoading(false);
    }
  };

  // Refresh role data
  const refreshRole = async () => {
    setLoading(true);
    await fetchUserRole();
  };

  // Effect to fetch role when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchUserRole();
    }
  }, [currentUser, authLoading]);

  // Computed properties
  const isAdmin = userRole?.roleId === 1;
  const isClient = userRole?.roleId === 2 || userRole?.roleId === undefined;

  const value = {
    userRole,
    loading,
    isAdmin,
    isClient,
    refreshRole
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}; 